(() => {
  'use strict';

  /*
   * ============================================================
   * CART ADDON
   * ============================================================
   */

  const state = {
    product: null,
    selectedColorProduct: null,
    selectedVariant: null,
    quantity: 1,
    loading: false,
    recommendationsLoading: false,
    lastCartSignature: null
  };


  const SELECTORS = {
    modal: '[data-cart-addon-modal]',
    modalBody: '[data-cart-addon-modal-body]',
    openButton: '[data-cart-addon-open]',
    closeButton: '[data-cart-addon-close]',

    colorDropdown:
      '[data-cart-addon-dropdown][data-dropdown-type="color"]',

    sizeDropdown:
      '[data-cart-addon-dropdown][data-dropdown-type="size"]',

    quantityMinus:
      '[data-cart-addon-quantity-minus]',

    quantityPlus:
      '[data-cart-addon-quantity-plus]',

    submit:
      '[data-cart-addon-submit]'
  };



  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }



  function formatMoney(cents) {
  const amount = Number(cents || 0) / 100;

  const currency =
    window.Shopify?.currency?.active ||
    document.documentElement.dataset.currency ||
    'USD';

  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code'
  }).format(amount);

  // Convert the non-breaking space to a normal space
  return formatted.replace(/\u00A0/g, ' ');
}
  // function formatMoney(cents) {
  //   const amount =
  //     Number(cents || 0) / 100;

  //   if (
  //     window.Shopify &&
  //     typeof window.Shopify.formatMoney === 'function'
  //   ) {
  //     return window.Shopify.formatMoney(
  //       Number(cents || 0)
  //     );
  //   }

  //   return amount.toLocaleString(
  //     undefined,
  //     {
  //       style: 'currency',
  //       currency: 'USD'
  //     }
  //   );
  // }


  function getModal() {
    return document.querySelector(
      SELECTORS.modal
    );
  }


  function getModalBody() {
    const modal = getModal();

    return modal
      ? modal.querySelector(
          SELECTORS.modalBody
        )
      : null;
  }


  function getRoot() {
    return (
      window.Shopify?.routes?.root ||
      '/'
    );
  }


  /*
   * ============================================================
   * CART SIGNATURE
   * ============================================================
   *
   * This creates a unique value for the
   * current cart.
   *
   * It changes when:
   *
   * - Product is added
   * - Product is removed
   * - Quantity is increased
   * - Quantity is decreased
   * - Variant changes
   *
   * If nothing changed, the signature
   * remains the same and recommendations
   * are not requested again.
   */

  function getCartSignature(cart) {
    if (!cart?.items?.length) {
      return 'empty';
    }

    return cart.items
      .map(item => {
        return [
          item.key,
          item.product_id,
          item.variant_id,
          item.quantity
        ].join(':');
      })
      .sort()
      .join('|');
  }


  /*
   * ============================================================
   * GET ADDON DATA
   * ============================================================
   */

  function getAddonData(productId) {
    const script =
      document.querySelector(
        `[data-cart-addon-data="${productId}"]`
      );

    if (!script) {
      return null;
    }

    try {
      return JSON.parse(
        script.textContent
      );
    } catch (error) {
      console.error(
        '[Cart Addon] Unable to parse addon data:',
        error
      );

      return null;
    }
  }


  /*
   * ============================================================
   * VARIANT HELPERS
   * ============================================================
   */

  function getSizeOptionIndex(product) {
    if (!product?.options?.length) {
      return -1;
    }

    return product.options.findIndex(
      option => {
        const name =
          typeof option === 'string'
            ? option
            : option?.name;

        return (
          String(name || '')
            .trim()
            .toLowerCase() === 'size'
        );
      }
    );
  }


  function findFirstAvailableVariant(product) {
    if (!product?.variants?.length) {
      return null;
    }

    return (
      product.variants.find(
        variant => variant.available
      ) ||
      product.variants[0] ||
      null
    );
  }


  function findVariantBySize(
    product,
    size
  ) {
    if (!product?.variants?.length) {
      return null;
    }

    const sizeIndex =
      getSizeOptionIndex(product);

    /*
     * Product doesn't have a Size option.
     */
    if (sizeIndex === -1) {
      return findFirstAvailableVariant(
        product
      );
    }

    /*
     * Prefer available variant.
     */
    const availableVariant =
      product.variants.find(
        variant => {
          return (
            variant.available &&
            String(
              variant.options?.[
                sizeIndex
              ]
            ) === String(size)
          );
        }
      );

    if (availableVariant) {
      return availableVariant;
    }

    /*
     * Fallback to unavailable matching
     * variant.
     */
    return (
      product.variants.find(
        variant => {
          return (
            String(
              variant.options?.[
                sizeIndex
              ]
            ) === String(size)
          );
        }
      ) || null
    );
  }


  /*
   * ============================================================
   * LOAD ACTUAL SHOPIFY PRODUCT
   * ============================================================
   */

  async function loadProduct(
    handle,
    productId
  ) {
    try {
      const response =
        await fetch(
          `/products/${handle}.js`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json'
            },

            cache: 'no-store'
          }
        );

      if (!response.ok) {
        throw new Error(
          `Unable to load product: ${response.status}`
        );
      }

      const product =
        await response.json();

      /*
       * Get data generated from the
       * recommendation Liquid section.
       */
      const addonData =
        getAddonData(productId);

      /*
       * Attach variation products.
       */
      product.variationProducts =
        addonData?.variationProducts ||
        [];

      product.options =
        product.options || [];

      product.variants =
        product.variants || [];

      // console.log(
      //   '[Cart Addon] Product loaded:',
      //   product
      // );

      // console.log(
      //   '[Cart Addon] Options:',
      //   product.options
      // );

      // console.log(
      //   '[Cart Addon] Variants:',
      //   product.variants
      // );

      return product;

    } catch (error) {
      console.error(
        '[Cart Addon] loadProduct error:',
        error
      );

      /*
       * Fallback to Liquid data.
       */
      const addonData =
        getAddonData(productId);

      if (!addonData) {
        return null;
      }

      return {
        id: addonData.id,
        handle: addonData.handle,
        title: addonData.title,

        featured_image:
          addonData.featuredImage,

        options: [],
        variants: [],

        variationProducts:
          addonData.variationProducts ||
          []
      };
    }
  }


  /*
   * ============================================================
   * LOAD COLOR PRODUCT
   * ============================================================
   */

  async function loadColorProduct(
    colorData
  ) {
    if (!colorData?.handle) {
      return null;
    }

    try {
      const response =
        await fetch(
          `/products/${colorData.handle}.js`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json'
            },

            cache: 'no-store'
          }
        );

      if (!response.ok) {
        throw new Error(
          `Unable to load color product: ${response.status}`
        );
      }

      const product =
        await response.json();

      /*
       * Preserve color information from
       * recommendation Liquid.
       */
      product.variationValue =
        colorData.variationValue || '';

      product.swatchStyle =
        colorData.swatchStyle || '';

      product.options =
        product.options || [];

      product.variants =
        product.variants || [];

      // console.log(
      //   '[Cart Addon] Color product loaded:',
      //   product
      // );

      return product;

    } catch (error) {
      console.error(
        '[Cart Addon] Color product error:',
        error
      );

      return null;
    }
  }


  /*
   * ============================================================
   * RENDER CUSTOM DROPDOWN
   * ============================================================
   */

  function renderCustomDropdown(
    dropdown,
    options,
    selectedValue,
    type
  ) {
    if (!dropdown) {
      return;
    }

    const selectedOption =
      options.find(
        option =>
          String(option.value) ===
          String(selectedValue)
      );

    const selectedLabel =
      selectedOption?.label ||
      selectedValue ||
      `Select ${type}`;

    dropdown.innerHTML = `
      <button
        type="button"
        class="cart-addon-dropdown__trigger"
        data-cart-addon-dropdown-trigger
      >
        ${escapeHTML(selectedLabel)}
      </button>

      <div class="cart-addon-dropdown__menu">

        ${options
          .map(option => {
            return `
              <button
                type="button"
                class="cart-addon-dropdown__option ${
                  option.disabled
                    ? 'is-disabled'
                    : ''
                }"
                data-cart-addon-dropdown-option
                data-dropdown-type="${escapeHTML(
                  type
                )}"
                data-value="${escapeHTML(
                  option.value
                )}"
                ${
                  option.disabled
                    ? 'disabled'
                    : ''
                }
              >

                ${
                  option.swatchStyle
                    ? `
                      <span
                        class="cart-addon-color-swatch"
                        style="${escapeHTML(
                          option.swatchStyle
                        )}"
                      ></span>
                    `
                    : ''
                }

                <span>
                  ${escapeHTML(
                    option.label
                  )}
                </span>

              </button>
            `;
          })
          .join('')}

      </div>
    `;
  }


  /*
   * ============================================================
   * RENDER COLORS
   * ============================================================
   */

  function renderColors() {
    const modal = getModal();

    if (!modal) {
      return;
    }

    const dropdown =
      modal.querySelector(
        SELECTORS.colorDropdown
      );

    if (!dropdown) {
      return;
    }

    const variationProducts =
      state.product?.variationProducts ||
      [];

    /*
     * No colors.
     */
    if (!variationProducts.length) {
      dropdown.innerHTML = `
        <button
          type="button"
          class="cart-addon-dropdown__trigger"
          disabled
        >
          No color options
        </button>

        <div class="cart-addon-dropdown__menu"></div>
      `;

      return;
    }

    const selectedId =
      state.selectedColorProduct?.id;

    const options =
      variationProducts.map(
        colorProduct => {
          return {
            value:
              String(
                colorProduct.id
              ),

            label:
              colorProduct.variationValue ||
              colorProduct.title ||
              'Color',

            swatchStyle:
              colorProduct.swatchStyle ||
              '',

            disabled: false
          };
        }
      );

    renderCustomDropdown(
      dropdown,
      options,
      selectedId,
      'color'
    );
  }


  /*
   * ============================================================
   * RENDER SIZES
   * ============================================================
   */

  function renderSizes() {
    const modal = getModal();

    if (!modal) {
      return;
    }

    const dropdown =
      modal.querySelector(
        SELECTORS.sizeDropdown
      );

    if (!dropdown) {
      return;
    }

    const product =
      state.selectedColorProduct ||
      state.product;

    /*
     * No variants.
     */
    if (
      !product?.variants?.length
    ) {
      dropdown.innerHTML = `
        <button
          type="button"
          class="cart-addon-dropdown__trigger"
          disabled
        >
          None
        </button>

        <div class="cart-addon-dropdown__menu"></div>
      `;

      state.selectedVariant =
        null;

      return;
    }

    const sizeIndex =
      getSizeOptionIndex(product);

    /*
     * Product has no Size option.
     */
    if (sizeIndex === -1) {
      state.selectedVariant =
        findFirstAvailableVariant(
          product
        );

      dropdown.innerHTML = `
        <button
          type="button"
          class="cart-addon-dropdown__trigger"
          disabled
        >
          One size
        </button>

        <div class="cart-addon-dropdown__menu"></div>
      `;

      return;
    }

    /*
     * Build unique sizes.
     */
    const sizes = [];

    product.variants.forEach(
      variant => {
        const size =
          variant.options?.[
            sizeIndex
          ];

        if (!size) {
          return;
        }

        const value =
          String(size);

        const alreadyExists =
          sizes.some(
            item =>
              item.value === value
          );

        if (!alreadyExists) {
          sizes.push({
            value,

            available:
              !!variant.available
          });

        } else {
          const existing =
            sizes.find(
              item =>
                item.value === value
            );

          if (
            variant.available &&
            existing
          ) {
            existing.available =
              true;
          }
        }
      }
    );

    /*
     * No sizes found.
     */
    if (!sizes.length) {
      dropdown.innerHTML = `
        <button
          type="button"
          class="cart-addon-dropdown__trigger"
          disabled
        >
          None
        </button>

        <div class="cart-addon-dropdown__menu"></div>
      `;

      state.selectedVariant =
        null;

      return;
    }

    /*
     * Determine current selected size.
     */
    let selectedSize =
      state.selectedVariant
        ?.options?.[sizeIndex];

    const currentSizeExists =
      selectedSize &&
      sizes.some(
        size =>
          String(size.value) ===
          String(selectedSize)
      );

    if (!currentSizeExists) {
      const firstAvailable =
        sizes.find(
          size => size.available
        ) || sizes[0];

      selectedSize =
        firstAvailable.value;
    }

    /*
     * Find actual variant.
     */
    const selectedVariant =
      findVariantBySize(
        product,
        selectedSize
      );

    if (selectedVariant) {
      state.selectedVariant =
        selectedVariant;
    }

    /*
     * Build dropdown.
     */
    const options =
      sizes.map(size => {
        const variant =
          findVariantBySize(
            product,
            size.value
          );

        return {
          value:
            size.value,

          label:
            size.value,

          disabled:
            !variant?.available
        };
      });

    renderCustomDropdown(
      dropdown,
      options,
      selectedSize,
      'size'
    );
  }


  /*
   * ============================================================
   * RENDER QUANTITY
   * ============================================================
   */

  function renderQuantity() {
    const modal = getModal();

    if (!modal) {
      return;
    }

    const quantityInput =
      modal.querySelector(
        '[data-cart-addon-quantity]'
      );

    if (quantityInput) {
      quantityInput.value =
        state.quantity;
    }
  }


  /*
   * ============================================================
   * RENDER MODAL
   * ============================================================
   */

  function renderModal() {
    const body =
      getModalBody();

    if (!body) {
      return;
    }

    const product =
      state.selectedColorProduct ||
      state.product;

    if (!product) {
      return;
    }

    const image =
      product.featured_image ||
      product.featuredImage ||
      product.images?.[0] ||
      '';

    const title =
      product.title ||
      state.product?.title ||
      '';

    const price =
      state.selectedVariant?.price ??
      product.price ??
      0;

    const hasColors =
      !!state.product?.variationProducts
        ?.length;

    const hasSizes =
      getSizeOptionIndex(product) !== -1;

    const canAdd =
      !!state.selectedVariant &&
      !!state.selectedVariant.available &&
      !state.loading;

      body.innerHTML = `
  <div class="cart-addon-modal__product">

    ${
      image
        ? `
          <div class="cart-addon-modal__product-image">
            <img
              src="${escapeHTML(image)}"
              alt="${escapeHTML(title)}"
              loading="lazy"
            >
          </div>
        `
        : ''
    }

    <div class="cart-addon-modal__product-info">

      <div class="cart-addon-modal__product-title">
        ${escapeHTML(title)}
      </div>

      <div class="cart-addon-modal__product-price">
        ${formatMoney(price)}
      </div>

    </div>

  </div>


  <!-- Color + Size wrapper -->
  <div class="cart-addon-modal__options">

    ${
      hasColors
        ? `
          <div class="cart-addon-field">

            <label class="cart-addon-field__label">
              Color
            </label>

            <div
              class="cart-addon-dropdown"
              data-cart-addon-dropdown
              data-dropdown-type="color"
            ></div>

          </div>
        `
        : ''
    }


    <div class="cart-addon-field">

      <label class="cart-addon-field__label">
        Size
      </label>

      <div
        class="cart-addon-dropdown"
        data-cart-addon-dropdown
        data-dropdown-type="size"
      ></div>

    </div>

  </div>


  <!-- Quantity + Add item wrapper -->
  <div class="cart-addon-modal__actions">

    <div class="cart-addon-field quantity-feild">

      <label class="cart-addon-field__label cart-addon-quantity__label">
        Quantity
      </label>

      <div class="cart-addon-quantity">

        <button
          type="button"
          data-cart-addon-quantity-minus
          aria-label="Decrease quantity"
        >
          −
        </button>

        <input
          type="number"
          min="1"
          value="${state.quantity}"
          data-cart-addon-quantity
        >

        <button
          type="button"
          data-cart-addon-quantity-plus
          aria-label="Increase quantity"
        >
          +
        </button>

      </div>

    </div>


    <button
      type="button"
      class="cart-addon-modal__submit"
      data-cart-addon-submit
      ${canAdd ? '' : 'disabled'}
    >
      ${
        state.loading
          ? 'Adding...'
          : 'Add item'
      }
    </button>

  </div>
`;

    // body.innerHTML = `
    //   <div class="cart-addon-modal__product">

    //     ${
    //       image
    //         ? `
    //           <div class="cart-addon-modal__product-image">

    //             <img
    //               src="${escapeHTML(
    //                 image
    //               )}"
    //               alt="${escapeHTML(
    //                 title
    //               )}"
    //               loading="lazy"
    //             >

    //           </div>
    //         `
    //         : ''
    //     }

    //     <div class="cart-addon-modal__product-info">

    //       <div class="cart-addon-modal__product-title">
    //         ${escapeHTML(title)}
    //       </div>

    //       <div class="cart-addon-modal__product-price">
    //         ${formatMoney(price)}
    //       </div>

    //     </div>

    //   </div>


    //   ${
    //     hasColors
    //       ? `
    //         <div class="cart-addon-field">

    //           <label class="cart-addon-field__label">
    //             Color
    //           </label>

    //           <div
    //             class="cart-addon-dropdown"
    //             data-cart-addon-dropdown
    //             data-dropdown-type="color"
    //           ></div>

    //         </div>
    //       `
    //       : ''
    //   }


    //   <div class="cart-addon-field">

    //     <label class="cart-addon-field__label">
    //       Size
    //     </label>

    //     <div
    //       class="cart-addon-dropdown"
    //       data-cart-addon-dropdown
    //       data-dropdown-type="size"
    //     ></div>

    //   </div>


    //   <div class="cart-addon-field">

    //     <label class="cart-addon-field__label">
    //       Quantity
    //     </label>

    //     <div class="cart-addon-quantity">

    //       <button
    //         type="button"
    //         data-cart-addon-quantity-minus
    //         aria-label="Decrease quantity"
    //       >
    //         −
    //       </button>

    //       <input
    //         type="number"
    //         min="1"
    //         value="${state.quantity}"
    //         data-cart-addon-quantity
    //       >

    //       <button
    //         type="button"
    //         data-cart-addon-quantity-plus
    //         aria-label="Increase quantity"
    //       >
    //         +
    //       </button>

    //     </div>

    //   </div>


    //   <button
    //     type="button"
    //     class="cart-addon-submit"
    //     data-cart-addon-submit
    //     ${canAdd ? '' : 'disabled'}
    //   >
    //     ${
    //       state.loading
    //         ? 'Adding...'
    //         : 'Add item'
    //     }
    //   </button>
    // `;

    /*
     * Populate dropdowns.
     */
    renderColors();
    renderSizes();
    renderQuantity();

    /*
     * Re-check Add button after
     * renderSizes selected variant.
     */
    const submitButton =
      body.querySelector(
        SELECTORS.submit
      );

    if (submitButton) {
      const validVariant =
        !!state.selectedVariant &&
        !!state.selectedVariant.available;

      submitButton.disabled =
        !validVariant ||
        state.loading;

      submitButton.textContent =
        state.loading
          ? 'Adding...'
          : 'Add item';
    }
  }


  /*
   * ============================================================
   * SELECT COLOR
   * ============================================================
   */

  async function selectColor(
    productId
  ) {
    const variationProducts =
      state.product?.variationProducts ||
      [];

    const colorData =
      variationProducts.find(
        product =>
          String(product.id) ===
          String(productId)
      );

    if (!colorData) {
      return;
    }

    /*
     * Disable Add while color product
     * is loading.
     */
    const body =
      getModalBody();

    if (body) {
      const submit =
        body.querySelector(
          SELECTORS.submit
        );

      if (submit) {
        submit.disabled =
          true;
      }
    }

    const colorProduct =
      await loadColorProduct(
        colorData
      );

    if (!colorProduct) {
      console.error(
        '[Cart Addon] Unable to load selected color.'
      );

      return;
    }

    state.selectedColorProduct =
      colorProduct;

    state.selectedVariant =
      findFirstAvailableVariant(
        colorProduct
      );

    // console.log(
    //   '[Cart Addon] Selected color:',
    //   colorProduct
    // );

    // console.log(
    //   '[Cart Addon] Selected variant:',
    //   state.selectedVariant
    // );

    renderModal();
  }


  /*
   * ============================================================
   * SELECT SIZE
   * ============================================================
   */

  function selectSize(size) {
    const product =
      state.selectedColorProduct ||
      state.product;

    if (!product) {
      return;
    }

    const variant =
      findVariantBySize(
        product,
        size
      );

    if (!variant) {
      // console.log(
      //   '[Cart Addon] No variant for size:',
      //   size
      // );

      return;
    }

    state.selectedVariant =
      variant;

    // console.log(
    //   '[Cart Addon] Size selected:',
    //   size
    // );

    // console.log(
    //   '[Cart Addon] Selected variant:',
    //   variant
    // );

    renderModal();
  }


  /*
   * ============================================================
   * OPEN MODAL
   * ============================================================
   */

  async function openModal(
    productId,
    productHandle
  ) {
    if (state.loading) {
      return;
    }

    const modal =
      getModal();

    const body =
      getModalBody();

    if (!modal || !body) {
      // console.error(
      //   '[Cart Addon] Modal not found.'
      // );

      return;
    }

    body.innerHTML = `
      <div class="cart-addon-modal__loading">
        Loading...
      </div>
    `;

    modal.hidden = false;

    requestAnimationFrame(() => {
      modal.classList.add(
        'is-active'
      );
    });

    /*
     * Load recommended product.
     */
    const product =
      await loadProduct(
        productHandle,
        productId
      );

    if (!product) {
      body.innerHTML = `
        <div class="cart-addon-error">
          Unable to load product.
        </div>
      `;

      return;
    }

    /*
     * Reset state.
     */
    state.product =
      product;

    state.selectedColorProduct =
      null;

    state.selectedVariant =
      null;

    state.quantity =
      1;

    state.loading =
      false;

    /*
     * If recommended product has
     * color variations, select first.
     */
    if (
      product.variationProducts?.length
    ) {
      const firstColor =
        product.variationProducts[0];

      const colorProduct =
        await loadColorProduct(
          firstColor
        );

      if (colorProduct) {
        state.selectedColorProduct =
          colorProduct;

        state.selectedVariant =
          findFirstAvailableVariant(
            colorProduct
          );
      }

    } else {
      /*
       * No color variations.
       */
      state.selectedColorProduct =
        product;

      state.selectedVariant =
        findFirstAvailableVariant(
          product
        );
    }

    // console.log(
    //   '[Cart Addon] Initial selected variant:',
    //   state.selectedVariant
    // );

    renderModal();
  }


  /*
   * ============================================================
   * CLOSE MODAL
   * ============================================================
   */

  function closeModal() {
    const modal =
      getModal();

    if (!modal) {
      return;
    }

    modal.classList.remove(
      'is-active'
    );

    setTimeout(() => {
      if (
        !modal.classList.contains(
          'is-active'
        )
      ) {
        modal.hidden = true;
      }
    }, 250);
  }


  /*
   * ============================================================
   * CHANGE QUANTITY
   * ============================================================
   */

  function changeQuantity(
    amount
  ) {
    const current =
      Number(state.quantity) || 1;

    state.quantity =
      Math.max(
        1,
        current + amount
      );

    renderQuantity();
  }


  /*
   * ============================================================
   * ADD TO CART
   * ============================================================
   */

  async function addToCart() {
    if (
      !state.selectedVariant ||
      state.loading
    ) {
      return;
    }

    const variantId =
      Number(
        state.selectedVariant.id
      );

    const quantity =
      Number(state.quantity) || 1;

    if (!variantId) {
      alert(
        'Please select a valid product variant.'
      );

      return;
    }

    if (
      !state.selectedVariant.available
    ) {
      alert(
        'This variant is currently unavailable.'
      );

      return;
    }

    state.loading =
      true;

    const submitButton =
      getModal()?.querySelector(
        SELECTORS.submit
      );

    if (submitButton) {
      submitButton.disabled =
        true;

      submitButton.textContent =
        'Adding...';
    }

    try {
      const response =
        await fetch(
          `${getRoot()}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity
                }
              ]
            })
          }
        );

      if (!response.ok) {
        const errorData =
          await response
            .json()
            .catch(() => ({}));

        throw new Error(
          errorData.description ||
          errorData.message ||
          'Unable to add product'
        );
      }

      /*
       * Close addon panel.
       */
      closeModal();

      /*
       * Refresh cart drawer.
       *
       * loadCartRecommendations()
       * will detect the changed cart
       * and load recommendations again.
       */
      await updateCartDrawer();

    } catch (error) {
      console.error(
        '[Cart Addon] Add to cart error:',
        error
      );

      if (submitButton) {
        submitButton.disabled =
          false;

        submitButton.textContent =
          'Add item';
      }

      alert(
        error.message ||
        'Unable to add product to cart.'
      );

    } finally {
      state.loading =
        false;
    }
  }


  /*
   * ============================================================
   * UPDATE CART DRAWER
   * ============================================================
   */

  async function updateCartDrawer() {
    try {
      const root =
        getRoot();

      const response =
        await fetch(
          `${root}?sections=cart-drawer`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json'
            },

            cache: 'no-store'
          }
        );

      if (!response.ok) {
        throw new Error(
          'Failed to fetch updated cart drawer.'
        );
      }

      const sections =
        await response.json();

      const drawerHTML =
        sections['cart-drawer'];

      if (!drawerHTML) {
        return;
      }

      const parser =
        new DOMParser();

      const doc =
        parser.parseFromString(
          drawerHTML,
          'text/html'
        );

      /*
       * Only replace cart items.
       */
      const newItems =
        doc.querySelector(
          '.cart-drawer__items'
        );

      const currentItems =
        document.querySelector(
          '#cart-drawer .cart-drawer__items'
        );

      if (
        !newItems ||
        !currentItems
      ) {
        return;
      }

      currentItems.innerHTML =
        newItems.innerHTML;

      /*
       * Update drawer class.
       */
      const currentDrawer =
        document.querySelector(
          '#cart-drawer'
        );

      if (currentDrawer) {
        const newDrawer =
          doc.querySelector(
            '#cart-drawer'
          );

        if (newDrawer) {
          currentDrawer.classList.toggle(
            'drawer--center-body',
            newDrawer.classList.contains(
              'drawer--center-body'
            )
          );
        }
      }

      /*
       * Update cart count.
       */
      const newCartCount =
        doc.querySelector(
          '[data-cart-count]'
        ) ||
        doc.querySelector(
          '.cart-count-bubble'
        );

      const currentCartCount =
        document.querySelector(
          '[data-cart-count]'
        ) ||
        document.querySelector(
          '.cart-count-bubble'
        );

      if (
        newCartCount &&
        currentCartCount
      ) {
        currentCartCount.innerHTML =
          newCartCount.innerHTML;
      }

      /*
       * Reload recommendations once.
       *
       * The function itself checks the
       * cart signature, so the same
       * cart will never trigger another
       * recommendation request.
       */
      await loadCartRecommendations();

      /*
       * IMPORTANT:
       *
       * Do NOT dispatch cart:updated
       * here.
       *
       * That can create a loop with
       * other cart scripts.
       */

    } catch (error) {
      console.error(
        '[Cart Addon] Cart drawer update failed:',
        error
      );
    }
  }


  /*
   * ============================================================
   * LOAD CART RECOMMENDATIONS
   * ============================================================
   */

  async function loadCartRecommendations(
    force = false
  ) {
    /*
     * Prevent duplicate requests.
     */
    if (
      state.recommendationsLoading
    ) {
      // console.log(
      //   '[Cart Addon] Recommendation request already running.'
      // );

      return;
    }

    const widget =
      document.querySelector(
        '[data-cart-addon-widget]'
      );

    if (!widget) {
      console.log(
        '[Cart Addon] Widget not found.'
      );

      return;
    }

    const productsContainer =
      widget.querySelector(
        '[data-cart-addon-products]'
      );

    if (!productsContainer) {
      console.log(
        '[Cart Addon] Products container not found.'
      );

      return;
    }

    const root =
      getRoot();

    state.recommendationsLoading =
      true;

    try {
      /*
       * Get current cart.
       */
      const cartResponse =
        await fetch(
          `${root}cart.js`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json'
            },

            cache: 'no-store'
          }
        );

      if (!cartResponse.ok) {
        throw new Error(
          'Unable to load cart.'
        );
      }

      const cart =
        await cartResponse.json();

      /*
       * Create unique signature.
       */
      const cartSignature =
        getCartSignature(cart);

      // console.log(
      //   '[Cart Addon] Cart signature:',
      //   cartSignature
      // );

      /*
       * IMPORTANT:
       *
       * If cart has not changed,
       * don't call recommendation API.
       */
      if (
        !force &&
        state.lastCartSignature ===
          cartSignature
      ) {
        console.log(
          '[Cart Addon] Cart has not changed. Skipping recommendations.'
        );

        return;
      }

      /*
       * Store current cart state.
       */
      state.lastCartSignature =
        cartSignature;

      /*
       * Cart empty.
       */
      if (!cart.items?.length) {
        widget.hidden =
          true;

        productsContainer.innerHTML =
          '';

        return;
      }

      /*
       * Show loading.
       */
      productsContainer.innerHTML = `
        <div class="cart-addon-loading">
          Loading...
        </div>
      `;

      /*
        * Try cart products one by one.
        *
        * If the first cart product has no
        * usable recommendations, try the
        * next cart product.
        */
        const addonWrapper =
          document.querySelector(
            '.addon-wrapper'
          );

        const cartProductIds =
          (cart.items || []).map(
            item => String(item.product_id)
          );

        let recommendationCards = [];

        /*
        * Try each product in the cart
        * until we find usable recommendations.
        */
        for (const cartItem of cart.items) {
          const productId =
            cartItem.product_id;

          if (!productId) {
            continue;
          }

          console.log(
            '[Cart Addon] Trying recommendations for cart product:',
            productId
          );

          const url =
            `${root}recommendations/products` +
            `?section_id=complete-the-fit-recommendations` +
            `&product_id=${productId}` +
            `&limit=4` +
            `&intent=complementary`;

          try {
            const response =
              await fetch(
                url,
                {
                  method: 'GET',

                  headers: {
                    Accept:
                      'text/html'
                  },

                  cache: 'no-store'
                }
              );

            if (!response.ok) {
              console.warn(
                '[Cart Addon] Recommendation request failed for product:',
                productId,
                response.status
              );

              continue;
            }

            const html =
              await response.text();

            const wrapper =
              document.createElement('div');

            wrapper.innerHTML =
              html;

            const recommendations =
              wrapper.querySelector(
                '.complete-the-fit-recommendations'
              );

            /*
            * This cart product has no
            * recommendation section.
            * Try the next cart product.
            */
            if (!recommendations) {
              console.log(
                '[Cart Addon] No recommendations for product:',
                productId
              );

              continue;
            }

            /*
            * Get recommendation cards.
            */
            const cards =
              Array.from(
                recommendations.querySelectorAll(
                  '.complete-fit-product'
                )
              );

            /*
            * Remove products that are already
            * present in the cart.
            */
            const availableCards =
              cards.filter(
                card => {
                  const recommendedProductId =
                    card.dataset.productId;

                  return (
                    recommendedProductId &&
                    !cartProductIds.includes(
                      String(
                        recommendedProductId
                      )
                    )
                  );
                }
              );

            /*
            * If this product only recommends
            * products already in cart, try
            * the next cart product.
            */
            if (!availableCards.length) {
              console.log(
                '[Cart Addon] All recommendations already in cart for product:',
                productId
              );

              continue;
            }

            /*
            * We found usable recommendations.
            *
            * Take maximum 3 and STOP searching.
            */
            recommendationCards =
              availableCards.slice(
                0,
                3
              );

            console.log(
              '[Cart Addon] Using recommendations from cart product:',
              productId
            );

            break;

          } catch (error) {
            /*
            * If one recommendation request
            * fails, continue with the next
            * cart product.
            */
            console.error(
              '[Cart Addon] Recommendation error for product:',
              productId,
              error
            );

            continue;
          }
        }

        /*
        * No usable recommendations were
        * found from ANY cart product.
        */
        if (!recommendationCards.length) {
          console.log(
            '[Cart Addon] No usable recommendations found from any cart product.'
          );

          widget.hidden =
            true;

          if (addonWrapper) {
            addonWrapper.hidden =
              true;
          }

          productsContainer.innerHTML =
            '';

          return;
        }

        /*
        * Show addon widget.
        */
        widget.hidden =
          false;

        if (addonWrapper) {
          addonWrapper.hidden =
            false;
        }

        productsContainer.innerHTML =
          '';

        console.log(
          '[Cart Addon] Final addon products:',
          recommendationCards
        );

          // console.log(
          //   '[Cart Addon] Recommended cards:',
          //   recommendationCards
          // );

          /*
          * Product IDs currently in cart.
          */
          // const cartProductIds =
          //   (cart.items || []).map(
          //     item =>
          //       String(
          //         item.product_id
          //       )
          //   );

          /*
          * Remove products already in cart.
          */
          recommendationCards =
            recommendationCards.filter(
              card => {
                const recommendedProductId =
                  card.dataset.productId;

                return (
                  recommendedProductId &&
                  !cartProductIds.includes(
                    String(
                      recommendedProductId
                    )
                  )
                );
              }
            );

          /*
          * Maximum 3 addon products.
          */
          recommendationCards =
            recommendationCards.slice(
              0,
              3
            );

          console.log(
            '[Cart Addon] Final addon products:',
            recommendationCards
          );

          /*
          * Nothing left to show.
          */
          if (
            !recommendationCards.length
          ) {
            widget.hidden =
              true;

            productsContainer.innerHTML =
              '';

            return;
          }

          /*
          * Show widget.
          */
          widget.hidden =
            false;

          productsContainer.innerHTML =
            '';

      /*
       * Render cards.
       */
      recommendationCards.forEach(
        card => {
          const recommendedProductId =
            card.dataset.productId ||
            '';

          const productHandle =
            card.dataset.productHandle ||
            '';

          const variantId =
            card.dataset.variantId ||
            '';

          const imageElement =
            card.querySelector(
              '[data-product-image]'
            );

          const titleElement =
            card.querySelector(
              '[data-product-title]'
            );

          const priceElement =
            card.querySelector(
              '[data-recommended-price]'
            );

          const productImage =
            imageElement?.src ||
            '';

          const productImageAlt =
            imageElement?.alt ||
            '';

          const productTitle =
            titleElement
              ?.textContent
              ?.trim() ||
            '';

          const productPrice =
            priceElement
              ?.textContent
              ?.trim() ||
            '';

          /*
           * Color variation information.
           */
          const variationProducts =
            [];

          card
            .querySelectorAll(
              '[data-color-option]'
            )
            .forEach(
              option => {
                variationProducts.push({
                  id:
                    option.dataset.productId ||
                    '',

                  handle:
                    option.dataset.productHandle ||
                    '',

                  variantId:
                    option.dataset.variantId ||
                    '',

                  variationValue:
                    option.dataset.colorValue ||
                    '',

                  image:
                    option.dataset.productImage ||
                    '',

                  imageAlt:
                    option.dataset.productImageAlt ||
                    '',

                  title:
                    option.dataset.productTitle ||
                    '',

                  price:
                    option.dataset.productPrice ||
                    '',

                  swatchStyle:
                    option.dataset.swatchStyle ||
                    ''
                });
              }
            );

          const productData = {
            id:
              recommendedProductId,

            handle:
              productHandle,

            title:
              productTitle,

            featuredImage:
              productImage,

            featuredImageAlt:
              productImageAlt,

            variantId:
              variantId,

            variationProducts:
              variationProducts
          };

          const productHTML = `
            <div
              class="cart-addon-product"
              data-cart-addon-product
            >

              <div class="cart-addon-product__image">

                ${
                  productImage
                    ? `
                      <img
                        src="${escapeHTML(
                          productImage
                        )}"
                        alt="${escapeHTML(
                          productImageAlt ||
                          productTitle
                        )}"
                        loading="lazy"
                        width="80"
                        height="80"
                      >
                    `
                    : ''
                }

              </div>

              <div class="cart-addon-product__content">

                <div
                  class="cart-addon-product__title"
                >
                  ${escapeHTML(
                    productTitle
                  )}
                </div>

                <div
                  class="cart-addon-product__price"
                >
                  ${escapeHTML(
                    productPrice
                  )}
                </div>

              </div>

              <button
                type="button"
                class="cart-addon-product__add"
                data-cart-addon-open
                data-product-id="${escapeHTML(
                  recommendedProductId
                )}"
                data-product-handle="${escapeHTML(
                  productHandle
                )}"
              >
                Add
              </button>

              <script
                type="application/json"
                data-cart-addon-data="${escapeHTML(
                  recommendedProductId
                )}"
              >${JSON.stringify(
                productData
              )}</script>

            </div>
          `;

          productsContainer.insertAdjacentHTML(
            'beforeend',
            productHTML
          );
        }
      );

      // console.log(
      //   '[Cart Addon] Successfully rendered:',
      //   recommendationCards.length
      // );

    } catch (error) {
      console.error(
        '[Cart Addon] Recommendation error:',
        error
      );

      widget.hidden =
        true;

      productsContainer.innerHTML =
        '';

    } finally {
      state.recommendationsLoading =
        false;
    }
  }


  /*
   * ============================================================
   * CLICK EVENTS
   * ============================================================
   *
   * Event delegation is used because
   * cart drawer HTML is replaced after
   * cart updates.
   */

  document.addEventListener(
    'click',
    async event => {

      /*
       * Open addon.
       */
      const openButton =
        event.target.closest(
          SELECTORS.openButton
        );

      if (openButton) {
        const productId =
          openButton.dataset.productId;

        const productHandle =
          openButton.dataset.productHandle;

        if (
          productId &&
          productHandle
        ) {
          await openModal(
            productId,
            productHandle
          );
        }

        return;
      }


      /*
       * Close addon.
       */
      const closeButton =
        event.target.closest(
          SELECTORS.closeButton
        );

      if (closeButton) {
        closeModal();

        return;
      }


      /*
       * Dropdown trigger.
       */
      const dropdownTrigger =
        event.target.closest(
          '[data-cart-addon-dropdown-trigger]'
        );

      if (dropdownTrigger) {
        const dropdown =
          dropdownTrigger.closest(
            '.cart-addon-dropdown'
          );

        if (!dropdown) {
          return;
        }

        /*
         * Close other dropdowns.
         */
        document
          .querySelectorAll(
            '.cart-addon-dropdown.is-open'
          )
          .forEach(
            openDropdown => {
              if (
                openDropdown !==
                dropdown
              ) {
                openDropdown.classList.remove(
                  'is-open'
                );
              }
            }
          );

        dropdown.classList.toggle(
          'is-open'
        );

        return;
      }


      /*
       * Dropdown option.
       */
      const dropdownOption =
        event.target.closest(
          '[data-cart-addon-dropdown-option]'
        );

      if (dropdownOption) {
        if (
          dropdownOption.disabled
        ) {
          return;
        }

        const type =
          dropdownOption.dataset
            .dropdownType;

        const value =
          dropdownOption.dataset
            .value;

        const dropdown =
          dropdownOption.closest(
            '.cart-addon-dropdown'
          );

        if (dropdown) {
          dropdown.classList.remove(
            'is-open'
          );
        }

        if (type === 'color') {
          await selectColor(value);
        }

        if (type === 'size') {
          selectSize(value);
        }

        return;
      }


      /*
       * Quantity minus.
       */
      const minusButton =
        event.target.closest(
          SELECTORS.quantityMinus
        );

      if (minusButton) {
        changeQuantity(-1);

        return;
      }


      /*
       * Quantity plus.
       */
      const plusButton =
        event.target.closest(
          SELECTORS.quantityPlus
        );

      if (plusButton) {
        changeQuantity(1);

        return;
      }


      /*
       * Add item.
       */
      const submitButton =
        event.target.closest(
          SELECTORS.submit
        );

      if (submitButton) {
        await addToCart();

        return;
      }


      /*
       * Click outside dropdown.
       */
      if (
        !event.target.closest(
          '.cart-addon-dropdown'
        )
      ) {
        document
          .querySelectorAll(
            '.cart-addon-dropdown.is-open'
          )
          .forEach(
            dropdown => {
              dropdown.classList.remove(
                'is-open'
              );
            }
          );
      }
    }
  );


  /*
   * ============================================================
   * QUANTITY INPUT
   * ============================================================
   */

  document.addEventListener(
    'input',
    event => {
      const input =
        event.target.closest(
          '[data-cart-addon-quantity]'
        );

      if (!input) {
        return;
      }

      let value =
        parseInt(
          input.value,
          10
        );

      if (
        Number.isNaN(value) ||
        value < 1
      ) {
        value = 1;
      }

      state.quantity =
        value;
    }
  );


  /*
   * ============================================================
   * ESCAPE KEY
   * ============================================================
   */

  document.addEventListener(
    'keydown',
    event => {
      if (
        event.key !== 'Escape'
      ) {
        return;
      }

      const modal =
        getModal();

      if (
        modal?.classList.contains(
          'is-active'
        )
      ) {
        closeModal();
      }
    }
  );


  /*
   * ============================================================
   * INITIAL LOAD
   * ============================================================
   *
   * Recommendations load ONCE.
   */

  function initCartAddon() {
    requestAnimationFrame(() => {
      loadCartRecommendations();
    });
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initCartAddon
    );
  } else {
    initCartAddon();
  }


document.addEventListener('cart:change', () => {
  console.log('[Cart Addon] cart:change detected.');

  setTimeout(async () => {
    try {
      await loadCartRecommendations(true);

      console.log(
        '[Cart Addon] Recommendations reloaded after cart change.'
      );
    } catch (error) {
      console.error(
        '[Cart Addon] Recommendation reload failed:',
        error
      );
    }
  }, 100);
});

  /*
   * ============================================================
   * SHOPIFY SECTION LOAD
   * ============================================================
   *
   * Shopify can reload the section.
   *
   * The cart signature prevents
   * unnecessary recommendation requests.
   */

  document.addEventListener(
    'shopify:section:load',
    event => {
      if (
        event.target?.querySelector(
          '[data-cart-addon-widget]'
        )
      ) {
        loadCartRecommendations();
      }
    }
  );

})();




document.addEventListener('click', function (event) {

  const removeButton = event.target.closest(
    '[data-line-item-remove]'
  );

  if (!removeButton) return;

  event.preventDefault();

  const lineItem = removeButton.closest('line-item');

  if (!lineItem) return;

  const removeLink = lineItem.querySelector(
    'a[data-remove-btn]'
  );

  if (!removeLink) return;

  removeLink.click();

});


// document.addEventListener('click', function (event) {
//   const removeButton = event.target.closest('[data-line-item-remove]');

//   if (!removeButton) return;

//   event.preventDefault();

//   const lineItem = removeButton.closest('line-item');

//   if (!lineItem) return;

//   const removeLink = lineItem.querySelector(
//     'line-item-quantity a[href*="/cart/change"]'
//   );

//   if (!removeLink) return;

//   removeLink.click();
// });